<script setup lang="ts">
import type { DescriptionItem } from '@fantastic-admin/components'
import type { AppDetailItem, AppDetailVariant } from './detailTypes'
import { FaDescriptions } from '@fantastic-admin/components'
import { useClipboard } from '@vueuse/core'

defineOptions({
  name: 'AppDetailList',
})

const props = withDefaults(defineProps<{
  items: AppDetailItem[]
  columns?: 1 | 2
  emptyText?: string
  labelWidth?: string | number
  variant?: AppDetailVariant
}>(), {
  columns: 1,
  emptyText: '-',
  labelWidth: '104px',
  variant: 'bordered',
})

function formatValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return props.emptyText
  }
  return String(value)
}

type ResolvedDetailItem = DescriptionItem<{
  source: AppDetailItem
}>

const descriptionItems = computed<ResolvedDetailItem[]>(() => props.items.map((item, index) => ({
  key: `item-${index}`,
  label: item.label,
  value: formatValue(item.value),
  span: item.span,
  valueClass: item.valueClass,
  source: item,
})))

const descriptionDirection = computed(() => props.variant === 'vertical' ? 'vertical' : 'horizontal')

function getValueSlotName(index: number) {
  return `value-item-${index}`
}

function getLabelSlotName(index: number) {
  return `label-item-${index}`
}

// 未显式配置图标时按常见后台字段语义选择图标，保持详情页标签视觉一致。
function resolveLabelIcon(item: AppDetailItem) {
  if (item.icon) {
    return item.icon
  }
  const label = item.label.toLowerCase()
  const iconRules: Array<[string[], string]> = [
    [['手机', '电话'], 'i-ri:smartphone-line'],
    [['邮箱', '邮件'], 'i-ri:mail-line'],
    [['时间', '日期', '生日'], 'i-ri:calendar-event-line'],
    [['ip', '地区', '地址'], 'i-ri:map-pin-line'],
    [['金额', '成交额', '余额'], 'i-ri:money-cny-circle-line'],
    [['等级', '积分', '星座'], 'i-ri:star-line'],
    [['来源', '方式'], 'i-ri:git-branch-line'],
    [['职业'], 'i-ri:briefcase-line'],
    [['签名', '简介', '介绍'], 'i-ri:file-text-line'],
    [['账号', '昵称', '姓名', '用户', 'uuid', 'id'], 'i-ri:user-line'],
  ]
  return iconRules.find(([keywords]) => keywords.some(keyword => label.includes(keyword)))?.[1]
    || 'i-ri:information-line'
}

const { copy } = useClipboard()

async function handleCopy(item: AppDetailItem) {
  const copyText = item.copyValue || formatValue(item.value)
  if (!copyText || copyText === props.emptyText) {
    useFaToast().warning('暂无可复制内容')
    return
  }

  try {
    await copy(copyText)
    useFaToast().success(item.copySuccessText || `${item.label}已复制`)
  }
  catch {
    useFaToast().error('复制失败，请稍后重试')
  }
}
</script>

<template>
  <FaDescriptions
    :items="descriptionItems"
    :column="props.columns"
    :border="props.variant === 'bordered'"
    :direction="descriptionDirection"
    :empty-text="props.emptyText"
    :label-width="props.labelWidth"
    size="sm"
    label-class="detail-label"
    value-class="detail-value"
  >
    <template
      v-for="(item, index) in props.items"
      :key="`label-${item.label}-${index}`"
      #[getLabelSlotName(index)]
    >
      <div class="detail-label-content">
        <FaIcon :name="resolveLabelIcon(item)" class="detail-label-icon" />
        <span>{{ item.label }}</span>
      </div>
    </template>
    <template
      v-for="(item, index) in props.items"
      :key="`${item.label}-${index}`"
      #[getValueSlotName(index)]
    >
      <div class="leading-6 flex gap-2 break-all items-start" :class="item.valueClass">
        <span class="break-all">{{ formatValue(item.value) }}</span>
        <FaTooltip v-if="item.copyable" text="复制">
          <FaButton
            variant="ghost"
            size="icon-sm"
            class="detail-copy-button"
            @click.stop="handleCopy(item)"
          >
            <FaIcon name="i-ri:file-copy-line" />
          </FaButton>
        </FaTooltip>
      </div>
    </template>
  </FaDescriptions>
</template>

<style scoped>
.detail-copy-button {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: oklch(var(--foreground) / 50%);
}

:deep(.detail-label) {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  color: oklch(var(--muted-foreground) / 78%);
  letter-spacing: 0.01em;
}

.detail-label-content {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.detail-label-icon {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

:deep(.detail-value) {
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.65;
}
</style>
